import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronRight,
  User,
  LogOut,
  Info,
  Shield,
  FileText,
  Settings as SettingsIcon,
  Mail,
  BarChart2,
  Bell,
  BellOff,
  List,
  Camera,
} from 'lucide-react';

import { useNavigate } from 'react-router-dom'; 
import { useAuth } from '../context/AuthContext'; 
import ThemeToggle from '../components/ThemeToggle';
import { usePushNotifications } from '../hooks/usePushNotifications';
import TutorialSettings from '../components/TutorialSettings';
import api, { getOnboardingStatus, completeTutorial, resolveUploadUrl } from '../api';
import { usePullToRefresh } from '../hooks/usePullToRefresh';

export default function SettingsPage({ appVersion = '1.0.0' }) {
  const navigate = useNavigate(); 
  const { currentUser, logout } = useAuth();
  const [showSettingsTutorial, setShowSettingsTutorial] = useState(false);
  const [loadingTutorial, setLoadingTutorial] = useState(true);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { isSubscribed, subscribeToPush, unsubscribeFromPush } = usePushNotifications();

  // Aggressive CSS hiding for Tawk widget
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      iframe[title="chat widget"], 
      .tawk-min-container,
      .tawk-max-container {
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
        transform: scale(0) !important;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      body.tawk-visible iframe[title="chat widget"],
      body.tawk-visible .tawk-min-container,
      body.tawk-visible .tawk-max-container {
        opacity: 1 !important;
        visibility: visible !important;
        pointer-events: auto !important;
        transform: scale(1) !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      logout();
    } catch (error) {
      console.error('Logout error:', error);
      logout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleToggleNotifications = () => {
    if (isSubscribed) {
      if (window.confirm("Are you sure you want to disable push notifications?")) {
        unsubscribeFromPush();
      }
    } else {
      subscribeToPush();
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const response = await getOnboardingStatus();
      const tutorialData = response.data; 
      if (tutorialData.hasSeenSettingsTutorial === false) {
        setShowSettingsTutorial(true);
      }
    } catch (err) {
      console.error('Could not fetch tutorial status', err);
    } finally {
      setLoadingTutorial(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Silent pull-to-refresh (mobile)
  const containerRef = usePullToRefresh(fetchStatus);

  const generalSettingsOptions = [
    {
      icon: User,
      label: 'Profile',
      description: 'Manage your personal information, email, and account details.',
      action: () => navigate('/dashboard/settings/profile'), 
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-600/20 dark:text-blue-400',
    },
    {
      icon: BarChart2,
      label: 'Performance Metrics',
      description: 'Generate and share your trading matrix summary.',
      action: () => navigate('/dashboard/settings/informatrix'),
      color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-600/20 dark:text-emerald-400',
    },
    {
      icon: Mail,
      label: 'Submit a Ticket',
      description: 'Send an email to our support team for complex issues.',
      action: () => window.location.href = 'mailto:tickets@edge-090tyy.p.tawk.email', 
      color: 'bg-teal-100 text-teal-600 dark:bg-teal-600/20 dark:text-teal-400',
    },
    {
      icon: Shield,
      label: 'Privacy',
      description: 'Learn about your privacy settings and data protection.',
      action: () => navigate('/privacy'),
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-600/20 dark:text-purple-400',
    },
    {
      icon: FileText,
      label: 'Terms and Conditions',
      description: 'Read our terms of service and user agreement.',
      action: () => navigate('/terms'),
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-600/20 dark:text-orange-400',
    },
  ];

  const appVersionOption = {
    icon: Info,
    label: 'App Version',
    description: `Current version: ${appVersion}`,
    action: () => setIsVersionModalOpen(true),
    color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-600/20 dark:text-indigo-400',
  };

  const signOutOption = {
    icon: LogOut,
    label: 'Sign Out',
    description: 'Sign out of your account on this device',
    action: handleLogout,
    color: 'bg-red-100 text-red-600 dark:bg-red-600/20 dark:text-red-400',
  };

  return (
    <div
      ref={containerRef}
      className="no-scrollbar h-full bg-slate-50 dark:bg-gray-900 p-4 md:p-6 pb-20 max-w-4xl mx-auto transition-colors duration-300 overflow-y-auto overflow-x-hidden"
    >
      
      {/* Sticky compact bar — hero block scrolls beneath it */}
      <header className="sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 pt-12 md:pt-3 pb-3 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Settings</h1>
      </header>

      <div className="pt-8 md:pt-6 pb-6 mb-6 md:mb-5 flex flex-col items-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl blur-2xl opacity-20 dark:opacity-50 transition-opacity"></div>
            
            <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-purple-600 rounded-3xl shadow-2xl transform transition-transform duration-300 hover:scale-105">
              <SettingsIcon size={36} className="text-white drop-shadow-lg" />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 dark:from-blue-400 dark:via-purple-400 dark:to-purple-500 mb-4 tracking-tight">
            Settings
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400 text-center max-w-lg text-lg leading-relaxed">
            Manage your application preferences and account settings
          </p>
      </div>

      <div className="space-y-8">

        {/* --- ACCOUNT / PROFILE PHOTO SECTION --- */}
        <section>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 px-1">Account</h2>
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none p-5">
            <div className="flex items-center gap-4">
              {currentUser?.avatar ? (
                <img
                  src={resolveUploadUrl(currentUser.avatar)}
                  alt={currentUser?.name || 'Profile'}
                  className="w-16 h-16 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="w-16 h-16 shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                  {(currentUser?.name || currentUser?.email || 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 dark:text-white truncate">
                  {currentUser?.name || 'Trader'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {currentUser?.email || ''}
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* --- NOTIFICATIONS SECTION --- */}
        <section>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 px-1">Notifications</h2>
          <div className="space-y-3">
            
            {/* Push Toggle Button */}
            <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
              <button
                onClick={handleToggleNotifications}
                className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 group-hover:scale-110 ${
                    isSubscribed 
                      ? 'bg-green-100 text-green-600 dark:bg-green-600/20 dark:text-green-400' 
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-600/20 dark:text-gray-400'
                  }`}>
                    {isSubscribed ? <Bell size={22} /> : <BellOff size={22} />}
                  </div>
                  <div className="text-left">
                    <span className="text-gray-900 dark:text-white font-medium block">
                      {isSubscribed ? 'Push Notifications Enabled' : 'Enable Push Notifications'}
                    </span>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {isSubscribed ? 'Tap to disable alerts for this device.' : 'Get real-time updates on your device.'}
                    </p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-all" />
              </button>
            </div>

            {/* View Notifications History Button */}
            <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
              <button
                onClick={() => navigate('/dashboard/notifications')}
                className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 group-hover:scale-110 bg-indigo-100 text-indigo-600 dark:bg-indigo-600/20 dark:text-indigo-400">
                    <List size={22} />
                  </div>
                  <div className="text-left">
                    <span className="text-gray-900 dark:text-white font-medium block">
                      Notification Center
                    </span>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      View your past notifications and activity.
                    </p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-all" />
              </button>
            </div>

          </div>
        </section>

        {/* --- GENERAL SECTION --- */}
        <section>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 px-1">General</h2>
          <div className="space-y-3">
            {generalSettingsOptions.map(({ icon: Icon, label, description, action, color }) => (
              <div
                key={label}
                className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none"
              >
                <button
                  onClick={action}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group active:scale-[0.98]"
                  aria-label={`Navigate to ${label}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center transition-all duration-200 group-hover:scale-110`}>
                      <Icon size={22} />
                    </div>
                    <div className="text-left">
                      <span className="text-gray-900 dark:text-white font-medium block">{label}</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 group-hover:translate-x-1 transition-all duration-200" />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* --- APPEARANCE SECTION --- */}
        <section>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 px-1">Appearance</h2>
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm p-5 rounded-2xl border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none flex items-center justify-between">
            <div>
              <span className="text-gray-900 dark:text-white font-medium block">Theme</span>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Toggle between light and dark mode</p>
            </div>
            <ThemeToggle />
          </div>
        </section>

        {/* --- SIGN OUT SECTION --- */}
        <section>
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
            <button
              onClick={signOutOption.action}
              disabled={isLoggingOut}
              className={`w-full p-5 flex items-center justify-between text-left hover:bg-red-50 dark:hover:bg-gray-700/50 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]`}
              aria-label={signOutOption.label}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${signOutOption.color} flex items-center justify-center transition-all duration-200 group-hover:scale-110`}>
                  {isLoggingOut ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <signOutOption.icon size={22} />
                  )}
                </div>
                <div className="text-left">
                  <span className="text-red-600 dark:text-white font-medium block">
                    {isLoggingOut ? 'Signing out...' : signOutOption.label}
                  </span>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{signOutOption.description}</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 group-hover:translate-x-1 transition-all duration-200" />
            </button>
          </div>
        </section>

        {/* --- VERSION SECTION --- */}
        {/* Added pb-10 for extra space below the button */}
        <section className="pt-2 pb-10">
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
            <button
              onClick={appVersionOption.action}
              className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group active:scale-[0.98]"
              aria-label={appVersionOption.label}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${appVersionOption.color} flex items-center justify-center transition-all duration-200 group-hover:scale-110`}>
                  <appVersionOption.icon size={22} />
                </div>
                <div className="text-left">
                  <span className="text-gray-900 dark:text-white font-medium block">{appVersionOption.label}</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{appVersionOption.description}</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 group-hover:translate-x-1 transition-all duration-200" />
            </button>
          </div>
        </section>
      </div>

      {/* Version Modal */}
      {isVersionModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white p-6 rounded-2xl shadow-2xl max-w-sm w-full border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Info size={28} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">App Version</h3>
                <p className="text-gray-500 dark:text-gray-400">Current version: {appVersion}</p>
              </div>
            </div>
            <button
              onClick={() => setIsVersionModalOpen(false)}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {!loadingTutorial && (
        <TutorialSettings 
          isVisible={showSettingsTutorial} 
          onClose={async () => {
            setShowSettingsTutorial(false);
            try {
              await completeTutorial('settings');
            } catch (err) {
              console.error('Failed to mark tutorial as complete', err);
            }
          }} 
        />
      )}
    </div>
  );
}